import JoditEditor from 'jodit-react';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Button from '../../components/ui/Button';
import Loader from '../../components/ui/Loader';
import Modal, { ModalBody, ModalFooter, ModalHeader } from '../../components/ui/Modal';
import SimpleLoader from '../../components/ui/SimpleLoader';
import { useApiReload } from '../../context/apiRelaod';
import { useAuth } from '../../context/authContext';
import useSaveBtn from '../../hooks/useSaveBtn';
import {
	DownloadNote,
	editNoteDetails,
	GetSoapBYId,
	PatientFetch,
	FormModelOption,
	FormCreate,
	viewModelPdf,
} from '../../services/apiService';
import { dateFormat } from '../../utils/priceFormat.util';
import ChatPage from '../ChatPage/Chat.page';
import { useResourceUsage } from '../../context/ResourceUsageContext';
type ContentKeys = 'consent' | 'subjective' | 'objective' | 'assessment' | 'treatment' | 'plan';

type NoteDetails = {
	consent: string;
	subjective: string;
	objective: string;
	assessment: string;
	plan: string;
	treatment: string;
};
export type NoteValues = {
	noteId: any;
	noteDetails: NoteDetails;
};

const SoapDetails = () => {
	const editor = useRef(null);
	const navigate = useNavigate();
	const { patientId, caseId, soapId } = useParams();
	const { state } = useLocation();
	const { patient, caseData, soapData, selectedSoapNoteId } = state || {};
	const { patientID, setPatientID } = useApiReload();
	const { onLogout } = useAuth();
	const { Patient_call, Patient_loading, Patient_response } = PatientFetch();
	const [loadingnote, setLoadingnote] = useState(false);

	// State for AI chat visibility
	const [showAiAssistant, setShowAiAssistant] = useState(false);

	const { call_SoapDetailResponse } = GetSoapBYId();
	const { loading, noteDetails_edit } = editNoteDetails();
	const { DownloadCall } = DownloadNote();

	// Form-related state and API calls
	const [noteFormData, setNoteFormData] = useState<any[]>([]);
	const [isFormLoading, setIsFormLoading] = useState(false);
	const { call: formCreateView, loading: formCreateViewLoading } = FormModelOption();
	const { call: formCreate, loading: formCreateLoading } = FormCreate();
	const { call: formViewPdf, loading: formViewPdfLoading } = viewModelPdf();
	const [loadingFormIndex, setLoadingFormIndex] = useState<number | null>(null);
	const [activeFormId, setActiveFormId] = useState<string>('');
	const [isUpdateModalOpen, setUpdateModalOpen] = useState(false);
	const [showFormsDrawer, setShowFormsDrawer] = useState(false);
	const [modalData, setModalData] = useState<{
		s3_bucket?: string;
		s3_key?: string;
		formId?: string;
	} | null>(null);
	const [showPreviewModal, setShowPreviewModal] = useState(false);
	const [previewFormData, setPreviewFormData] = useState<{
		formId: string;
		formName: string;
	} | null>(null);
	const { setUsage } = useResourceUsage();

	if (!patient || !caseData || !soapData) {
		return (
			<div className='flex h-screen items-center justify-center'>
				{/* <Loader /> */}
				<SimpleLoader />
			</div>
		);
	}

	useEffect(() => {
		if (patientId && caseId && soapId) {
			Patient_call(patientId).catch((err) => {
				if (err?.status === 409) {
					onLogout(err?.status);
				} else {
					toast.error(err?.data?.msg || err.message);
					console.log(err);
				}
			});
			call_SoapDetailResponse(`${soapId}`).subscribe({
				next: (v) => {
					if (v.data.noteDetails === '') {
						setLoadingnote(true);
					} else {
						setLoadingnote(false);
						setContent(JSON.parse(v.data.noteDetails) as NoteDetails);
					}
				},
				error: (err) => {
					if (err?.status === 409) {
						onLogout(err?.status);
					}
					console.log(err);
				},
			});
		}
	}, [patientId, caseId, soapId]);

	useEffect(() => {
		if (Patient_response) {
			setPatientID({
				firstName: Patient_response.data.firstName,
				lastName: Patient_response.data.lastName,
				email: Patient_response.data.email,
				id: Patient_response.data.id,
				moblieNo: Patient_response.data.mobile,
			});
		}
	}, [Patient_response, setPatientID]);

	if (!soapData) {
		return (
			<div className='flex h-screen items-center justify-center'>
				<p>No SOAP note found.</p>
			</div>
		);
	}

	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isPopupOpen, setIsPopupOpen] = useState(false);
	const [isChanged, setIsChanged] = useState(false);
	const [isUpdated, setIsUpdated] = useState(false);
	const [isSaving, setIsSaving] = useState<boolean>(false);
	const [currentSection, setCurrentSection] = useState<ContentKeys | ''>('');
	const [currentContent, setCurrentContent] = useState('');

	// Add state to track which sections are expanded (all open by default)
	const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
		consent: true,
		subjective: true,
		objective: true,
		assessment: true,
		treatment: true,
		plan: true,
	});

	useEffect(() => {
		setIsSaving(loading);
	}, [loading]);

	const { saveBtnText } = useSaveBtn({
		isNewItem: false,
		isSaving,
		isDirty: isUpdated,
	});

	const [content, setContent] = useState<NoteDetails>({
		consent: '',
		subjective: '',
		objective: '',
		assessment: '',
		treatment: '',
		plan: '',
	});

	const [noteValues, setNoteValues] = useState<NoteValues>({
		noteId: soapId,
		noteDetails: { ...content },
	});

	const handleEdit = (section: ContentKeys) => {
		setCurrentSection(section);
		const htmlContent = content[section] || '';
		setCurrentContent(htmlContent);
		setIsModalOpen(true);
	};

	const handleDownload = (e: any) => {
		DownloadCall(selectedSoapNoteId).subscribe({
			next: (value: any) => {
				const url = URL.createObjectURL(value);
				const a = document.createElement('a');
				a.href = url;
				a.download = `${selectedSoapNoteId}.pdf`;
				document.body.appendChild(a);
				a.click();
				URL.revokeObjectURL(url);
				document.body.removeChild(a);
				toast.success('Document downloaded successfully');
			},
			error: (error: any) => {
				if (error?.status === 409) {
					onLogout(error?.status);
				}
				console.error('Error downloading the PDF:', error);
				toast.error('Failed to download document');
			},
		});
	};

	const handleUpdate = () => {
		const updated = {
			...noteValues,
			noteDetails: { ...content },
		};
		setNoteValues(updated);
		noteDetails_edit(updated).subscribe({
			next: (res: any) => {
				toast.success(res.msg);
			},
			error: (e: any) => {
				if (e?.status === 409) {
					onLogout(e?.status);
				}
				toast.error(e?.data?.msg || e.message);
			},
		});
		setIsUpdated(false);
		setIsChanged(false);
	};

	const handleSave = () => {
		if (currentSection) {
			const updatedContent = currentContent;
			const newContent = { ...content, [currentSection]: updatedContent };
			setContent((prevContent) => {
				const newContent = { ...prevContent, [currentSection]: updatedContent };

				setNoteValues((prevValues) => ({
					...prevValues,
					noteDetails: { ...newContent },
				}));
				return newContent;
			});
			const updated = {
				...noteValues,
				noteDetails: { ...newContent },
			};

			noteDetails_edit(updated).subscribe({
				next: (res: any) => {
					setIsModalOpen(false);
					toast.success(res.msg);
				},
				error: (e: any) => {
					if (e?.status === 409) {
						onLogout(e?.status);
					}
					toast.error(e?.data?.msg || e.message);
				},
			});
			setIsUpdated(false);
		}
	};

	const handleCopy = (section: ContentKeys) => {
		const html = content[section];
		const withoutNumbers = html
			.replace(/<ol[^>]*>/g, '')
			.replace(/<\/ol>/g, '')
			.replace(/<li[^>]*>/g, '- ')
			.replace(/<\/li>/g, '\n')
			.replace(/<p[^>]*>/g, '\n')
			.replace(/<\/p>/g, '\n');
		const plainText = withoutNumbers.replace(/<[^>]+>/g, '');
		navigator.clipboard.writeText(plainText);
		toast.success(
			`${section.charAt(0).toUpperCase() + section.slice(1)} content copied to clipboard!`,
		);
	};

	const handleCloseModal = () => {
		setIsChanged(false);
		setIsModalOpen(false);
	};

	const handlePopupOpen = () => setIsPopupOpen(true);

	// Function to get section title with better formatting
	const getSectionTitle = (section: string): string => {
		const sectionTitles: Record<string, string> = {
			consent: 'Consent',
			subjective: 'Subjective',
			objective: 'Objective',
			assessment: 'Assessment',
			treatment: 'Treatment Plan',
			plan: 'Plan',
		};
		return sectionTitles[section] || section.charAt(0).toUpperCase() + section.slice(1);
	};

	// Helper function to check if a section has content
	const hasContent = (section: ContentKeys): boolean => {
		return !!content[section]?.trim();
	};

	// Function to toggle section expansion
	const toggleSection = (section: string) => {
		setExpandedSections((prev) => ({
			...prev,
			[section]: !prev[section],
		}));
	};

	// Toggle AI assistant
	const toggleAiAssistant = () => {
		setShowAiAssistant((prev) => !prev);
		// Add a small delay to adjust the layout after the panel appears
		setTimeout(() => {
			window.dispatchEvent(new Event('resize'));
		}, 300);
	};

	// Load forms on initial render to update the badge count
	useEffect(() => {
		if (soapId) {
			loadFormData();
		}
	}, [soapId]);

	// Toggle forms drawer
	const toggleFormsDrawer = () => {
		setShowFormsDrawer(!showFormsDrawer);
		if (!showFormsDrawer && soapId) {
			loadFormData();
		}
	};

	// Form handlers
	const handleCreateForm = (formId: string, formName: string) => {
		setActiveFormId(formId);
		const data = {
			noteId: soapId,
			formId: formId,
			formUrl: 'https://example.com/form',
		};
		
		formCreate(data).subscribe({
			next(res: any) {
				toast.success(`${formName} created successfully.`);
				// Reload forms
				loadFormData();
				setUsage((prev) => ({
					...prev,
					formsCount: res?.data?.formsCount || prev.formsCount,
				}));
			},
			error(e: any) {
				if (e?.status === 409) {
					onLogout(e?.status);
				}
				toast.error(e?.data?.msg || e.message);
			},
		});
	};

	const handleViewForm = (formUrl: any, formId: string, index: number, formCreate: boolean = false, needToUseOld: boolean = false) => {
		setLoadingFormIndex(index);
		setActiveFormId(formId);
		formUrl = JSON.parse(formUrl);
		formUrl.formId = formId;
		formUrl.forceCreate = formCreate;
		formUrl.noteId = soapId;
		
		setModalData(formUrl);
		
		formViewPdf(JSON.stringify(formUrl)).subscribe({
			next: (res: any) => {
				if (res?.data?.updated && !needToUseOld) {
					setUpdateModalOpen(true);
				} else {
					const byteArray = res?.data?.pdf?.data;
					const blob = new Blob([new Uint8Array(byteArray)], { type: 'application/pdf' });
					const url = URL.createObjectURL(blob);
					setUpdateModalOpen(false);
					window.open(url, '_blank');
					setLoadingFormIndex(null);
					setTimeout(() => {
						URL.revokeObjectURL(url);
					}, 5000);
				}
			},
			error: (error: any) => {
				setLoadingFormIndex(null);
				if (error?.status === 409) {
					onLogout(error?.status);
				}
				console.error('Error in Opening the PDF:', error);
			},
		});
	};

	const handlePreviewForm = (formId: string, formName: string) => {
		setPreviewFormData({
			formId,
			formName
		});
		setShowPreviewModal(true);
	};

	const loadFormData = () => {
		if (soapId) {
			setIsFormLoading(true);
			formCreateView({ noteId: soapId }).subscribe({
				next(res: any) {
					setNoteFormData(res?.data || []);
					setIsFormLoading(false);
				},
				error(err) {
					setIsFormLoading(false);
					if (err?.status === 409) {
						onLogout(err?.status);
					}
					console.error(err);
				}
			});
		}
	};

	return (
		<div className='min-h-screen bg-gray-50 pb-20 dark:bg-slate-900'>
			<div
				className={`container mx-auto px-4 pb-20 pt-6 transition-all duration-300 ease-in-out 
				${showAiAssistant ? 'pr-[400px] md:pr-[500px]' : ''} 
				${showFormsDrawer ? 'pl-0 pr-[400px] md:pr-[500px]' : ''}`}>
				{/* Header section - not sticky, scrolls away normally */}
				<div className='mb-6 rounded-lg bg-white p-6 shadow-md dark:bg-slate-800'>
					<div className='mb-4 flex flex-col justify-between gap-4 md:flex-row md:items-center'>
						<div>
							<h1 className='mb-2 text-2xl font-bold text-gray-900 dark:text-white'>
								{soapData.noteType || 'SOAP Note'}
							</h1>
							<div className='flex flex-wrap gap-3 text-sm text-gray-500 dark:text-gray-400'>
								<span className='flex items-center gap-1'>
									<svg
										xmlns='http://www.w3.org/2000/svg'
										className='h-4 w-4'
										fill='none'
										viewBox='0 0 24 24'
										stroke='currentColor'>
										<path
											strokeLinecap='round'
											strokeLinejoin='round'
											strokeWidth={2}
											d='M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z'
										/>
									</svg>
									Created: {dateFormat(soapData?.createdAt)}
								</span>
								<span className='flex items-center gap-1'>
									<svg
										xmlns='http://www.w3.org/2000/svg'
										className='h-4 w-4'
										fill='none'
										viewBox='0 0 24 24'
										stroke='currentColor'>
										<path
											strokeLinecap='round'
											strokeLinejoin='round'
											strokeWidth={2}
											d='M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z'
										/>
									</svg>
									Updated:{' '}
									{dateFormat(soapData?.updatedAt || soapData?.createdAt)}
								</span>
								<span className='flex items-center gap-1'>
									<svg
										xmlns='http://www.w3.org/2000/svg'
										className='h-4 w-4'
										fill='none'
										viewBox='0 0 24 24'
										stroke='currentColor'>
										<path
											strokeLinecap='round'
											strokeLinejoin='round'
											strokeWidth={2}
											d='M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z'
										/>
									</svg>
									Patient: {patientID?.firstName} {patientID?.lastName}
								</span>
							</div>
						</div>
						<div className='flex flex-wrap gap-2'>
							<Button
								variant='outline'
								color='gray'
								colorIntensity='500'
								className='flex items-center gap-2 text-sm'
								onClick={() => {
									isUpdated ? handlePopupOpen() : navigate(-1);
								}}>
								<svg
									xmlns='http://www.w3.org/2000/svg'
									className='h-4 w-4'
									fill='none'
									viewBox='0 0 24 24'
									stroke='currentColor'>
									<path
										strokeLinecap='round'
										strokeLinejoin='round'
										strokeWidth={2}
										d='M10 19l-7-7m0 0l7-7m-7 7h18'
									/>
								</svg>
								Back
							</Button>

							<Button
								variant="solid"
								colorIntensity="600"
								className="flex items-center gap-2 text-sm primary-bg relative"
								onClick={toggleFormsDrawer}>
								<svg
									xmlns='http://www.w3.org/2000/svg'
									className='h-4 w-4'
									fill='none'
									viewBox='0 0 24 24'
									stroke='currentColor'>
									<path
										strokeLinecap='round'
										strokeLinejoin='round'
										strokeWidth={2}
										d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
									/>
								</svg>
								Forms
								{noteFormData && noteFormData.length > 0 && (
									<span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-xs text-white">
										{noteFormData.filter(form => form.isCreated).length}
									</span>
								)}
							</Button>

							<Button
								variant="solid"
								colorIntensity="600"
								className="flex items-center gap-2 text-sm primary-bg"
								onClick={(e) => handleDownload(e)}>
								<svg
									xmlns='http://www.w3.org/2000/svg'
									className='h-4 w-4'
									fill='none'
									viewBox='0 0 24 24'
									stroke='currentColor'>
									<path
										strokeLinecap='round'
										strokeLinejoin='round'
										strokeWidth={2}
										d='M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4'
									/>
								</svg>
								Download
							</Button>
						</div>
					</div>

					<div className='grid grid-cols-1 gap-4 border-t border-gray-200 pt-4 dark:border-gray-700 md:grid-cols-3'>
						<div className='flex flex-col'>
							<span className='text-sm text-gray-500 dark:text-gray-400'>Email</span>
							<span className='text-gray-800 dark:text-gray-200'>
								{patientID?.email}
							</span>
						</div>
						<div className='flex flex-col'>
							<span className='text-sm text-gray-500 dark:text-gray-400'>Mobile</span>
							<span className='text-gray-800 dark:text-gray-200'>
								{patientID?.moblieNo}
							</span>
						</div>
						<div className='flex flex-col'>
							<span className='text-sm text-gray-500 dark:text-gray-400'>
								Case ID
							</span>
							<span className='text-gray-800 dark:text-gray-200'>
								{caseData?.caseName || caseData?.id}
							</span>
						</div>
					</div>
				</div>

				{/* SOAP Note Content */}
				<div className='transition-all duration-300 ease-in-out'>
					{loadingnote ? (
						<div className='flex justify-center py-20'>
							<SimpleLoader />
						</div>
					) : (
						<div className='mb-6 flex flex-col gap-3'>
							{Object.keys(content).map((section) => (
								<div
									key={section}
									className='overflow-hidden rounded-lg bg-white shadow-sm transition-all duration-200 dark:bg-slate-800'>
									<div
										className='flex cursor-pointer items-center justify-between border-b border-gray-200 bg-gray-50 px-6 py-4 dark:border-gray-700 dark:bg-slate-700'
										onClick={() => toggleSection(section)}>
										<div className='flex items-center gap-2'>
											<button className='p-1 text-gray-500 focus:outline-none dark:text-gray-400'>
												<svg
													xmlns='http://www.w3.org/2000/svg'
													className={`h-5 w-5 transition-transform duration-200 ${expandedSections[section] ? 'rotate-180' : ''}`}
													fill='none'
													viewBox='0 0 24 24'
													stroke='currentColor'>
													<path
														strokeLinecap='round'
														strokeLinejoin='round'
														strokeWidth={2}
														d='M19 9l-7 7-7-7'
													/>
												</svg>
											</button>
											<h2 className='font-semibold text-gray-900 dark:text-white'>
												{getSectionTitle(section)}
											</h2>
										</div>
										<div className='flex items-center gap-1'>
											<button
												className='rounded-full p-2 text-gray-500 transition-colors hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-300'
												title='Edit section'
												onClick={(e) => {
													e.stopPropagation();
													handleEdit(section as ContentKeys);
												}}>
												<svg
													xmlns='http://www.w3.org/2000/svg'
													className='h-5 w-5'
													fill='none'
													viewBox='0 0 24 24'
													stroke='currentColor'>
													<path
														strokeLinecap='round'
														strokeLinejoin='round'
														strokeWidth={2}
														d='M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z'
													/>
												</svg>
											</button>
											<button
												className='rounded-full p-2 text-gray-500 transition-colors hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-300'
												title='Copy content'
												onClick={(e) => {
													e.stopPropagation();
													handleCopy(section as ContentKeys);
												}}>
												<svg
													xmlns='http://www.w3.org/2000/svg'
													className='h-5 w-5'
													fill='none'
													viewBox='0 0 24 24'
													stroke='currentColor'>
													<path
														strokeLinecap='round'
														strokeLinejoin='round'
														strokeWidth={2}
														d='M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2'
													/>
												</svg>
											</button>
										</div>
									</div>

									{/* Collapsible content */}
									<div
										className={`overflow-hidden transition-all duration-300 ${
											expandedSections[section]
												? 'max-h-[2000px] opacity-100'
												: 'max-h-0 opacity-0'
										}`}>
										<div className='p-6'>
											{hasContent(section as ContentKeys) ? (
												<div
													className='prose max-w-none dark:prose-invert'
													dangerouslySetInnerHTML={{
														__html: content[section as ContentKeys],
													}}
												/>
											) : (
												<div className='py-4 text-center text-gray-400 dark:text-gray-500'>
													<p>No content yet</p>
													<button
														className='mx-auto mt-2 flex items-center gap-1 font-medium text-blue-500 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300'
														onClick={() =>
															handleEdit(section as ContentKeys)
														}>
														<svg
															xmlns='http://www.w3.org/2000/svg'
															className='h-4 w-4'
															fill='none'
															viewBox='0 0 24 24'
															stroke='currentColor'>
															<path
																strokeLinecap='round'
																strokeLinejoin='round'
																strokeWidth={2}
																d='M12 4v16m8-8H4'
															/>
														</svg>
														Add {getSectionTitle(section)}
													</button>
												</div>
											)}
										</div>
									</div>
								</div>
							))}
						</div>
					)}
				</div>
			</div>

			{/* AI Assistant Section */}
			{showAiAssistant && (
				<div className='fixed right-0 top-[80px] z-40 h-[calc(100%-70px)] w-[400px] translate-x-0 transform shadow-xl transition-all duration-300 ease-in-out md:w-[500px]'>
					<div className='flex h-full flex-col overflow-hidden bg-white dark:bg-slate-800'>
						<div className='flex items-center justify-between border-b border-gray-200 bg-gray-50 px-6 py-4 dark:border-gray-700 dark:bg-slate-700'>
							<h2 className='flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white'>
								<svg
									xmlns='http://www.w3.org/2000/svg'
									className='h-6 w-6 text-blue-500'
									fill='none'
									viewBox='0 0 24 24'
									stroke='currentColor'>
									<path
										strokeLinecap='round'
										strokeLinejoin='round'
										strokeWidth={2}
										d='M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z'
									/>
								</svg>
								AI Assistant
							</h2>
							<button
								className='text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
								onClick={toggleAiAssistant}>
								<svg
									xmlns='http://www.w3.org/2000/svg'
									className='h-6 w-6'
									fill='none'
									viewBox='0 0 24 24'
									stroke='currentColor'>
									<path
										strokeLinecap='round'
										strokeLinejoin='round'
										strokeWidth={2}
										d='M6 18L18 6M6 6l12 12'
									/>
								</svg>
							</button>
						</div>
						<div className='flex-1 overflow-auto'>
							<ChatPage soapNoteId={selectedSoapNoteId || soapId} />
						</div>
					</div>
				</div>
			)}

			{/* Forms Drawer */}
			<div 
				className={`fixed right-0 top-[80px] z-30 h-[calc(100%-70px)] w-[400px] bg-white shadow-xl transition-transform duration-300 ease-in-out dark:bg-slate-800 ${
					showFormsDrawer ? 'translate-x-0' : 'translate-x-full'
				} md:w-[500px]`}
			>
				<div className="flex h-full flex-col">
					<div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-4 py-4 dark:border-gray-700 dark:bg-slate-700">
						<h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white">
							<svg
								xmlns='http://www.w3.org/2000/svg'
								className='h-5 w-5'
								fill='none'
								viewBox='0 0 24 24'
								stroke='currentColor'>
								<path
									strokeLinecap='round'
									strokeLinejoin='round'
									strokeWidth={2}
									d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
								/>
							</svg>
							Forms for this note
						</h2>
						<button 
							onClick={toggleFormsDrawer}
							className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
						>
							<svg
								xmlns='http://www.w3.org/2000/svg'
								className='h-5 w-5'
								fill='none'
								viewBox='0 0 24 24'
								stroke='currentColor'>
								<path
									strokeLinecap='round'
									strokeLinejoin='round'
									strokeWidth={2}
									d='M6 18L18 6M6 6l12 12'
								/>
							</svg>
						</button>
					</div>
					
					<div className="flex-1 overflow-y-auto p-4">
						{isFormLoading ? (
							<div className="flex justify-center py-8">
								<SimpleLoader />
							</div>
						) : noteFormData && noteFormData.length > 0 ? (
							<div className="divide-y divide-gray-200 dark:divide-gray-700">
								{noteFormData.map((form, idx) => (
									<div key={form.formId} className="py-4">
										<div className="flex flex-col gap-3">
											<div>
												<h3 className="text-lg font-medium">{form.formName}</h3>
												{form.isCreated && (
													<div className="mt-1 text-sm text-green-600 flex items-center dark:text-green-400">
														<svg className="inline-block h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
															<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
														</svg>
														Generated
													</div>
												)}
											</div>
											<div>
												{form?.isCreated ? (
													<Button
														variant="solid"
														colorIntensity="500"
														className="primary-bg text-white w-full"
														isLoading={loadingFormIndex === idx && activeFormId === form.formId}
														onClick={() => handleViewForm(form.formUrl, form.formId, idx)}
													>
														View Form
													</Button>
												) : (
													<div className="flex gap-2">
														<Button
															variant="solid"
															colorIntensity="500"
															className="primary-bg text-white flex-1"
															isLoading={formCreateLoading && activeFormId === form.formId}
															onClick={() => handleCreateForm(form.formId, form.formName)}
														>
															Generate
														</Button>
														<Button
															variant="outline"
															color="blue"
															colorIntensity="500"
															className="flex-1"
															onClick={() => handlePreviewForm(form.formId, form.formName)}
														>
															Preview
														</Button>
													</div>
												)}
											</div>
										</div>
									</div>
								))}
							</div>
						) : (
							<div className="py-8 text-center text-gray-500">
								<svg 
									className="mx-auto h-12 w-12 text-gray-400 mb-3" 
									fill="none" 
									stroke="currentColor" 
									viewBox="0 0 24 24" 
									xmlns="http://www.w3.org/2000/svg"
								>
									<path 
										strokeLinecap="round" 
										strokeLinejoin="round" 
										strokeWidth={1.5} 
										d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" 
									/>
								</svg>
								<p className="text-lg font-medium">No forms available for this note</p>
								<p className="mt-1">Contact administrator if you believe this is an error</p>
							</div>
						)}
					</div>
				</div>
			</div>
			
			{/* Floating AI button - only show when AI assistant is closed */}
			{!showAiAssistant && (
				<div className='fixed bottom-4 right-4 z-30'>
					<Button
						variant="solid"
						colorIntensity="500"
						className="flex primary-bg items-center justify-center w-16 h-16 text-sm rounded-full p-3 shadow-xl bg-blue-500 hover:bg-blue-600"
						onClick={toggleAiAssistant}
					>
						<svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
						</svg>
						<span className='sr-only'>Ask AI</span>
					</Button>
				</div>
			)}

			{/* Forms toggle button - only show when forms drawer is closed */}
			{!showFormsDrawer && (
				<div className='fixed bottom-4 right-20 z-30'>
					<Button
						variant="solid"
						colorIntensity="500"
						className="flex primary-bg items-center justify-center w-16 h-16 text-sm rounded-full p-3 shadow-xl bg-blue-600 hover:bg-blue-700"
						onClick={toggleFormsDrawer}
					>
						<svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
						</svg>
						<span className='sr-only'>Forms</span>
						{noteFormData && noteFormData.length > 0 && (
							<span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-xs text-white">
								{noteFormData.filter(form => form.isCreated).length}
							</span>
						)}
					</Button>
				</div>
			)}

			{/* Forms Modal */}
			<Modal
				isOpen={isUpdateModalOpen}
				setIsOpen={setUpdateModalOpen}
				isCentered
				size='sm'
				closeOnOverlayClick={false}
				data={modalData}
			>
				<ModalHeader setIsOpen={setUpdateModalOpen}>Update Form</ModalHeader>
				<ModalBody className='pt-3'>
					<p className='mb-0 text-lg'>Do you want to regenerate form?</p>
				</ModalBody>
				<ModalFooter className='justify-end pt-3'>
					<Button
						variant='solid'
						color='gray'
						colorIntensity='500'
						className='cursor-pointer text-white'
						isLoading={formViewPdfLoading}
						onClick={() =>
							handleViewForm(
								JSON.stringify({
									s3_bucket: modalData?.s3_bucket,
									s3_key: modalData?.s3_key,
								}),
								modalData?.formId ?? '',
								1,
								false,
								true,
							)
						}>
						No
					</Button>
					<Button
						variant='solid'
						colorIntensity='600'
						className='primary-bg cursor-pointer text-white'
						isLoading={formViewPdfLoading}
						onClick={() =>
							handleViewForm(
								JSON.stringify({
									s3_bucket: modalData?.s3_bucket,
									s3_key: modalData?.s3_key,
								}),
								modalData?.formId ?? '',
								1,
								true,
								false,
							)
						}>
						Yes
					</Button>
				</ModalFooter>
			</Modal>

			{/* Edit Modal */}
			<Modal isOpen={isModalOpen} setIsOpen={setIsModalOpen} isCentered size='lg'>
				<ModalHeader
					setIsOpen={setIsModalOpen}
					className='border-b border-gray-200 dark:border-gray-700'>
					Edit {getSectionTitle(currentSection)}
				</ModalHeader>
				<ModalBody className='p-0'>
					<JoditEditor
						ref={editor}
						value={currentContent}
						onBlur={(newContent) => {
							setIsChanged(true);
							setCurrentContent(newContent);
						}}
						config={
							{
								height: 400,
								readonly: false,
								toolbar: true,
								buttons: [
									'bold',
									'italic',
									'underline',
									'strikeThrough',
									'subscript',
									'superscript',
									'font',
									'fontSize',
									'foreColor',
									'backColor',
									'align',
									'unorderedList',
									'orderedList',
									'outdent',
									'indent',
									'paragraph',
									'link',
									'image',
									'table',
									'undo',
									'redo',
									'quote',
									'fullscreen',
									'hr',
									'help',
									'emoji',
								],
								theme: 'default',
								statusbar: false,
								removeButtons: ['about'],
								addNewLine: false,
								cleanHTML: {
									cleanOnPaste: false,
									replaceNBSP: false,
								},
							} as any
						}
					/>
				</ModalBody>
				<ModalFooter className='flex justify-end gap-3 dark:border-gray-700'>
					<Button
						variant='outline'
						color='gray'
						colorIntensity='500'
						onClick={handleCloseModal}>
						Cancel
					</Button>
					<Button
						variant='solid'
						color='green'
						colorIntensity='600'
						isLoading={loading}
						onClick={handleSave}
						className='text-white'>
						Save Changes
					</Button>
				</ModalFooter>
			</Modal>

			{/* Form Preview Modal */}
			<Modal isOpen={showPreviewModal} setIsOpen={setShowPreviewModal} isCentered size='md'>
				<ModalHeader setIsOpen={setShowPreviewModal}>Form Preview: {previewFormData?.formName}</ModalHeader>
				<ModalBody className="p-4">
					<div className="flex flex-col items-center">
						<div className="mb-4 rounded border border-gray-200 p-2 shadow-sm">
							{/* Placeholder for form preview image - In a real implementation, this would be a dynamic image based on formId */}
							<div className="bg-gray-50 p-4 rounded">
								<div className="mx-auto w-full max-w-md">
									{/* Mock form layout */}
									<div className="mb-4 border-b-2 border-gray-800 pb-2">
										<h3 className="text-xl font-bold text-center">{previewFormData?.formName}</h3>
										<p className="text-center text-sm text-gray-500">Sample Form Layout</p>
									</div>
									
									<div className="mb-4 space-y-2">
										<div className="h-6 bg-gray-200 rounded w-full"></div>
										<div className="h-6 bg-gray-200 rounded w-3/4"></div>
										<div className="h-6 bg-gray-200 rounded w-1/2"></div>
									</div>
									
									<div className="mb-4 p-2 border border-gray-300 rounded">
										<div className="h-20 bg-gray-100 rounded"></div>
									</div>
									
									<div className="grid grid-cols-2 gap-3">
										<div className="h-8 bg-gray-200 rounded"></div>
										<div className="h-8 bg-gray-200 rounded"></div>
									</div>
								</div>
							</div>
						</div>
						<p className="text-sm text-gray-500 mb-4 text-center">
							This is a preview of the form layout. The actual form will include patient data from this note.
						</p>
						<div className="flex gap-3">
							<Button
								variant="outline"
								color="gray"
								colorIntensity="500"
								onClick={() => setShowPreviewModal(false)}
							>
								Close
							</Button>
							<Button
								variant="solid"
								colorIntensity="500"
								className="primary-bg text-white"
								onClick={() => {
									setShowPreviewModal(false);
									if (previewFormData) {
										handleCreateForm(previewFormData.formId, previewFormData.formName);
									}
								}}
							>
								Generate This Form
							</Button>
						</div>
					</div>
				</ModalBody>
			</Modal>

			{/* Unsaved Changes Modal */}
			<Modal isOpen={isPopupOpen} setIsOpen={setIsPopupOpen} isCentered size='sm'>
				<ModalHeader setIsOpen={setIsPopupOpen}>Unsaved Changes</ModalHeader>
				<ModalBody>
					<div className='mb-3 flex items-center gap-3 rounded-lg bg-amber-50 p-3 text-amber-600 dark:bg-amber-900/20 dark:text-amber-500'>
						<svg
							xmlns='http://www.w3.org/2000/svg'
							className='h-6 w-6'
							fill='none'
							viewBox='0 0 24 24'
							stroke='currentColor'>
							<path
								strokeLinecap='round'
								strokeLinejoin='round'
								strokeWidth={2}
								d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z'
							/>
						</svg>
						<span className='font-medium'>You have unsaved changes</span>
					</div>
					<p className='text-gray-600 dark:text-gray-300'>
						Do you want to save your changes before leaving this page?
					</p>
				</ModalBody>
				<ModalFooter className='justify-end gap-3 pt-3'>
					<Button
						variant='outline'
						color='gray'
						colorIntensity='500'
						className='cursor-pointer'
						onClick={() => {
							setIsPopupOpen(false);
							navigate(-1);
						}}>
						Don't Save
					</Button>
					<Button
						className='cursor-pointer text-white'
						color='green'
						variant='solid'
						colorIntensity='600'
						onClick={() => {
							setIsPopupOpen(false);
							handleUpdate();
							navigate(-1);
						}}>
						Save & Exit
					</Button>
				</ModalFooter>
			</Modal>
		</div>
	);
};

export default SoapDetails;
